/* eslint-disable react/prop-types */
import { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Card,
  Skeleton,
  Alert,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import { PieChart as PieChartIcon } from "@mui/icons-material";
import useGetStatisticData from "@/hooks/useGetStatisticData";
import useGetObservationStatistics from "@/hooks/useGetObservationStatistics";
import { useTranslation } from "react-i18next";

const datasetNameFieldMap = {
  ptwPerSite: "site_name",
  ptwPerWorkflow: "workflow_name",
  ptwPerAuthor: "username",
  ptwPerPerformer: "username",
  obsPerWorkflow: "workflow_name",
  obsPerAuthor: "username",
  obsPerSite: "site_name",
};

export default function PieChartComponent({
  dataType = "ptw",
  selectedDataset,
  isModalView = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const ptwData = useGetStatisticData();
  const obsData = useGetObservationStatistics();

  // Use theme colors instead of hardcoded colors
  const COLORS = theme.palette.dashboard.chart.colors;

  const { counts, isLoading, error } = dataType === "ptw" ? ptwData : obsData;

  const datasetOptions =
    dataType === "ptw"
      ? [
          { value: "ptwPerWorkflow", label: t("pieChart.ptwPerWorkflowLabel") },
          { value: "ptwPerSite", label: t("pieChart.ptwPerSiteLabel") },
          { value: "ptwPerAuthor", label: t("pieChart.ptwPerAuthorLabel") },
          {
            value: "ptwPerPerformer",
            label: t("pieChart.ptwPerPerformerLabel"),
          },
        ]
      : [
          { value: "obsPerWorkflow", label: t("pieChart.obsPerWorkflowLabel") },
          { value: "obsPerAuthor", label: t("pieChart.obsPerAuthorLabel") },
          { value: "obsPerSite", label: t("pieChart.obsPerSiteLabel") },
        ];

  const limitOptions = [
    { value: 3, label: t("pieChart.top3Label") },
    { value: 5, label: t("pieChart.top5Label") },
    { value: 10, label: t("pieChart.top10Label") },
    { value: 20, label: t("pieChart.top20Label") },
    { value: 9999, label: t("pieChart.showAllLabel") },
  ];

  const [internalSelectedDataset, setInternalSelectedDataset] = useState(
    selectedDataset || datasetOptions[0].value
  );
  const [limit, setLimit] = useState(limitOptions[1].value);

  useEffect(() => {
    if (selectedDataset) {
      setInternalSelectedDataset(selectedDataset);
    }
  }, [selectedDataset]);

  const chartContainerRef = useRef(null);
  const [outerRadius, setOuterRadius] = useState(100);

  useEffect(() => {
    const updateRadius = () => {
      if (chartContainerRef.current) {
        const { offsetWidth, offsetHeight } = chartContainerRef.current;
        const availableSize = Math.min(offsetWidth, offsetHeight);
        const calculatedRadius = Math.max(
          60,
          Math.min(200, availableSize * (isModalView ? 0.4 : 0.35))
        );
        setOuterRadius(calculatedRadius);
      }
    };

    updateRadius();
    window.addEventListener("resize", updateRadius);
    return () => window.removeEventListener("resize", updateRadius);
  }, [isModalView]);

  const pieData = useMemo(() => {
    const rawData = counts[internalSelectedDataset];
    const fieldName = datasetNameFieldMap[internalSelectedDataset];
    if (!rawData || !Array.isArray(rawData)) return [];

    const mapped = rawData.map((item) => ({
      name: item[fieldName] || t("pieChart.unknownLabel"),
      value: item.count || 0,
    }));
    mapped.sort((a, b) => b.value - a.value);

    if (
      internalSelectedDataset === "obsPerWorkflow" ||
      limit >= mapped.length ||
      limit === 9999
    ) {
      return mapped;
    }

    const topItems = mapped.slice(0, limit);
    const otherItems = mapped.slice(limit);
    const othersTotal = otherItems.reduce((sum, it) => sum + it.value, 0);
    return [
      ...topItems,
      { name: t("pieChart.othersLabel"), value: othersTotal },
    ];
  }, [counts, internalSelectedDataset, limit, t]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: isModalView ? 3 : 0,
          bgcolor: theme.palette.dashboard.background.card,
        }}
      >
        {isModalView && internalSelectedDataset !== "obsPerWorkflow" && (
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              bgcolor: theme.palette.dashboard.background.elevated,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
            <Skeleton
              variant="rectangular"
              width={150}
              height={56}
              sx={{ borderRadius: 1 }}
            />
          </Card>
        )}
        <Skeleton
          variant="circular"
          width={200}
          height={200}
          sx={{ mx: "auto" }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: isModalView ? 3 : 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t("pieChart.errorText")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("pieChart.errorSupportText")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (pieData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 3,
          flexDirection: "column",
          gap: 2,
          bgcolor: theme.palette.dashboard.background.card,
        }}
      >
        <PieChartIcon
          sx={{
            fontSize: 48,
            color: theme.palette.dashboard.text.muted,
          }}
        />
        <Typography
          color={theme.palette.dashboard.text.secondary}
          variant="body1"
          textAlign="center"
        >
          {t("pieChart.noDataText")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: theme.palette.dashboard.background.card,
      }}
      aria-label={t("pieChart.chartAria", {
        dataType,
        dataset: internalSelectedDataset,
      })}
    >
      {/* Controls - improved layout */}
      {isModalView && internalSelectedDataset !== "obsPerWorkflow" && (
        <Card
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" }, // column on mobile, row on ≥sm
            alignItems: "flex-start",
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: theme.palette.dashboard.background.subtle,
            boxShadow: theme.palette.dashboard.shadow.card,
            gap: 3, // space between the two flex items
          }}
        >
          {/* Left column: title + subtitle */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1, // take up remaining space
              minWidth: 0, // for text-overflow safety
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.dashboard.text.primary,
                fontSize: 16,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PieChartIcon sx={{ fontSize: 20 }} />
              {t("pieChart.chartConfigTitle")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.dashboard.text.secondary,
                mt: 1,
              }}
            >
              {t("pieChart.chartConfigSubtitle")}
            </Typography>
          </Box>

          {/* Right column: form controls */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 150, // or whatever your form needs
            }}
          >
            <FormControl variant="outlined" size="small">
              <InputLabel
                sx={{
                  color: theme.palette.dashboard.text.secondary,
                  fontSize: 14,
                }}
              >
                {t("pieChart.limitLabel")}
              </InputLabel>
              <Select
                value={limit}
                label={t("pieChart.limitLabel")}
                onChange={(e) => setLimit(e.target.value)}
                sx={{
                  bgcolor: theme.palette.dashboard.background.elevated,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                }}
              >
                {limitOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Card>
      )}

      {/* Chart Container */}
      <Box
        ref={chartContainerRef}
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: isModalView ? 400 : 150,
          p: 1,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {isModalView && (
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.dashboard.background.elevated,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  boxShadow: theme.palette.dashboard.shadow.card,
                  color: theme.palette.dashboard.text.primary,
                }}
                labelStyle={{
                  color: theme.palette.dashboard.text.primary,
                  fontWeight: 600,
                }}
              />
            )}
            {isModalView && (
              <Legend
                wrapperStyle={{
                  fontSize: 14,
                  color: theme.palette.dashboard.text.secondary,
                }}
              />
            )}
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              labelLine={false}
              label={
                isModalView
                  ? ({
                      percent,
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius =
                        innerRadius + (outerRadius - innerRadius) * 0.5;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill={theme.palette.dashboard.text.inverse}
                          textAnchor={x > cx ? "start" : "end"}
                          dominantBaseline="central"
                          fontSize={12}
                          fontWeight={600}
                        >
                          {`${(percent * 100).toFixed(0)}%`}
                        </text>
                      );
                    }
                  : false
              }
              outerRadius={outerRadius}
              fill={theme.palette.dashboard.chart.primary}
            >
              {pieData.map((entry, idx) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={COLORS[idx % COLORS.length]}
                  stroke={theme.palette.dashboard.background.card}
                  strokeWidth={2}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
